import { formatMoney } from '../money/index.js';

export class ApiError extends Error {
  status: number;
  code?: string;
  /** RFC 7807 problem-details extensions, verbatim from the refusal. */
  extensions?: Record<string, unknown>;

  constructor(args: {
    status: number;
    code?: string;
    message: string;
    extensions?: Record<string, unknown>;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.extensions = args.extensions;
  }
}

export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Read a refusal body into an `ApiError`.
 *
 * The one parse every non-ok response goes through — `apiFetch` and each of the
 * auth mutations, which used to hand-roll their own `{ code, message }` reader.
 * Those copies silently dropped `extensions`, so the "add a backend code and the
 * client needs no change" promise held only for calls routed through `apiFetch`.
 *
 * `fallbackMessage` is the caller's own phrase for a body that carries no
 * `message` or does not parse at all; `code` and `extensions` are simply absent
 * in that case, which sends `mapApiError` to the generic localized string.
 */
export async function apiErrorFromResponse(
  res: Response,
  fallbackMessage: string,
): Promise<ApiError> {
  let code: string | undefined;
  let message = fallbackMessage;
  // RFC 7807 `extensions` is where a refusal puts the structured facts behind
  // it — the minimum a deposit missed, for one. The catalog's `_detailed` key
  // interpolates them; without them it can only say "under the minimum".
  let extensions: Record<string, unknown> | undefined;
  try {
    const body = (await res.json()) as {
      code?: string;
      message?: string;
      extensions?: unknown;
    };
    code = body.code;
    message = body.message ?? message;
    if (body.extensions && typeof body.extensions === 'object' && !Array.isArray(body.extensions)) {
      extensions = body.extensions as Record<string, unknown>;
    }
  } catch {
    // ignore parse failure — the fallback message stands
  }
  return new ApiError({ status: res.status, code, message, extensions });
}

/**
 * i18next's `t`. The second argument is widened only as far as `defaultValue`
 * and interpolation values need it — every call site passes i18next's own `t`,
 * which already accepts both.
 */
export type Translator = (
  key: string,
  opts?: { defaultValue?: string } & Record<string, unknown>,
) => string;

/**
 * A money figure as it travels in `extensions`: minor units as a string, paired
 * with the currency that gives them their exponent. Without the pairing the
 * client has to assume 2dp, which renders a JPY or KRW figure at 100x. Mirrors
 * `moneyExtension` in `@b1fr0st/shared/money`.
 */
interface MoneyExtension {
  amount: string;
  currency: string;
}

/**
 * Deliberately narrower than `BigInt()`, which reads `0x10` as 16 and would let
 * a malformed figure render as a plausible-looking amount.
 */
const MINOR_UNITS = /^-?\d+$/;

/**
 * `Intl.NumberFormat` throws a RangeError for any currency that is not exactly
 * three alpha characters — `''`, `'TH'`, `'THBB'` and `'123'` all do. Since
 * `mapApiError` is called from inside `toast.error(...)` and `onError`, an
 * escaping throw does not degrade the message, it removes it: the player gets
 * no toast at all. And the value is not ours to trust — `getAgentCurrency`
 * returns `res.org?.currency` verbatim from tenancy with no shape check.
 *
 * Same rule the sibling module states at `money/index.ts`: a label is never
 * worth an exception (WEB-22).
 */
const CURRENCY_CODE = /^[A-Za-z]{3}$/;

/**
 * The shape a backend error code is allowed to have before it is used as an
 * i18next key.
 *
 * i18next resolves a key by walking the catalog with plain property access, so
 * it reaches `Object.prototype` too: a refusal carrying `code: "constructor"`
 * resolves to `function Object() { [native code] }`, and `code: "toString"` to
 * the function source — and `defaultValue` does not save it, because as far as
 * i18next is concerned the key WAS found. `code` is read verbatim out of the
 * response body (`apiErrorFromResponse`), so its value is not ours to trust,
 * the same reason `MINOR_UNITS` and `CURRENCY_CODE` above exist.
 *
 * Every prototype member is camelCase or underscore-prefixed, so requiring an
 * uppercase first character is what closes the hole; the rest of the pattern
 * just describes the codes we actually issue. The hyphenated tail is optional
 * because `INTERNAL` (`packages/shared/src/errors.ts`) is a real single-word
 * code, alongside `DOMAIN-NNN`, `RATE-LIMIT`, `AUTH-MFA-REQUIRED` and
 * `WALLET-PA-001`. `error.test.ts` holds the catalogs to it.
 */
const ERROR_CODE = /^[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)*$/;

/**
 * Codes that may be handed to `t()`. A code of any other shape is treated as if
 * the refusal carried none at all — the generic localized message.
 */
export function isErrorCode(code: string | undefined): code is string {
  return typeof code === 'string' && ERROR_CODE.test(code);
}

/** Both halves of the pair are validated, or the figure is dropped. */
function isMoneyExtension(value: unknown): value is MoneyExtension {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Partial<MoneyExtension>;
  return (
    typeof v.amount === 'string' &&
    typeof v.currency === 'string' &&
    MINOR_UNITS.test(v.amount) &&
    CURRENCY_CODE.test(v.currency)
  );
}

/**
 * Turn an error's extensions into i18next interpolation values.
 *
 * Money is formatted here rather than on the server because the currency symbol
 * and grouping are a display concern; the wire only ever carries minor units and
 * the currency code. A value that cannot be rendered is dropped rather than
 * guessed at, which sends the caller back to the figure-free message.
 */
/** Formats one money figure of an error; screens pass `useFormatMoney()` to show ledger money in the site currency. */
export type ErrorMoneyFormatter = (minor: bigint, currency: string) => string;

function interpolationValues(
  extensions: Record<string, unknown> | undefined,
  money: ErrorMoneyFormatter = formatMoney,
): Record<string, string> {
  const values: Record<string, string> = {};
  if (!extensions) return values;
  for (const [key, value] of Object.entries(extensions)) {
    if (isMoneyExtension(value)) {
      values[key] = money(BigInt(value.amount), value.currency);
    } else if (typeof value === 'string' || typeof value === 'number') {
      values[key] = String(value);
    }
  }
  return values;
}

/**
 * `money`, when given, formats the figures a refusal carries — pass
 * `useFormatMoney()` on a screen that shows money in the site's display currency,
 * so a minimum reads in kip on a kip site (#640). Omitted = the figure as sent.
 */
export function mapApiError(err: unknown, t: Translator, money?: ErrorMoneyFormatter): string {
  // Keys are namespace-qualified (`errors:`) so they resolve against the
  // dedicated `errors` namespace regardless of which page namespace the caller's
  // `t` is bound to. Without the prefix i18next looks for `errors.*` inside the
  // page namespace (auth/wallet/…) and returns the raw key.
  if (err instanceof NetworkError) return t('errors:network');
  if (err instanceof ApiError) {
    if (!isErrorCode(err.code)) return t('errors:unknown');
    // `defaultValue` is what stops an uncatalogued code reaching a player as the
    // literal string "FIN-306": i18next returns the KEY when it cannot resolve
    // one, so every backend code added without a matching catalog entry used to
    // surface raw. The backoffice has always passed a defaultValue here; the
    // player app did not. The fallback is the localized generic rather than
    // `err.message`, which is English and written for operators.
    const plain = t(`errors:${err.code}`, { defaultValue: t('errors:unknown') });

    // A refusal that carries structured facts gets a second, figure-bearing
    // sentence — but only if the catalog has one. Keeping it under a separate
    // `_detailed` key is what lets the plain key stay placeholder-free: a
    // refusal that arrives without extensions (an older service, or a currency
    // that could not be resolved) then renders a complete sentence instead of a
    // dangling `{{minDeposit}}`. It also means every other code in the catalog
    // is untouched — no `_detailed` entry, no change.
    const values = interpolationValues(err.extensions, money);
    if (Object.keys(values).length === 0) return plain;
    return t(`errors:${err.code}_detailed`, { ...values, defaultValue: plain });
  }
  return t('errors:unknown');
}

/**
 * `mapApiError` for a caller that would rather say nothing than say something
 * generic — the forced-logout notice on the login page, where "Something went
 * wrong. Please try again." is both untrue (nothing the player just did went
 * wrong) and unactionable, and where saying nothing is the behaviour that
 * shipped.
 *
 * Returns null for anything the catalog cannot name: a non-`ApiError`, an error
 * with no code or a code of an unrecognized shape, or a code with no entry. A
 * backend code added with a catalog entry needs no change here — which is the
 * whole point of the catalog.
 */
export function mapKnownApiError(err: unknown, t: Translator, money?: ErrorMoneyFormatter): string | null {
  if (!(err instanceof ApiError) || !isErrorCode(err.code)) return null;
  // The empty defaultValue is the probe: i18next echoes the key when it cannot
  // resolve one, so an absent entry is indistinguishable from a real message
  // without it.
  if (t(`errors:${err.code}`, { defaultValue: '' }) === '') return null;
  return mapApiError(err, t, money);
}

export function isRetryable(err: unknown): boolean {
  return err instanceof NetworkError;
}
